// "The run is over." The plate that says so, and the two ways out of it.
//
// The same plate, glow and arrival as `Celebration.tsx` — it imports that
// component rather than growing a second one, because a game that announces
// its best moment and its worst in two unrelated visual languages reads as two
// games. What differs is the `grave` tone (the glow goes cold, the plate
// settles instead of bouncing, and there is no shine), the token (a fallen
// plate or a rocket rather than a turning crystal), and the fact that there is
// nothing to *claim*: a celebration ends with one button because the reward is
// already yours, and this ends with a choice because the run is not.
//
// Three rules carried over from that file, all still load-bearing:
//
//  - **Nothing here may reflow the canvas.** The plate's `disableScrollLock`.
//  - **Everything that moves is a keyframe**, never a value in `sx`.
//  - **It takes the pointer**, unlike every other overlay — a click landing on
//    the board behind it would be aimed at something you cannot see.
//
// And one that is this file's own: **the copy is MDX**. It is prose, so it
// lives in `content/defeat/*.mdx` where a writer can open it; the numbers are
// drawn here from the run, so the sentences cannot drift from what happened.
// **No dismiss**: a stray click that closed this would leave a finished board
// with no way back to the menu.

import { MDXProvider } from "@mdx-js/react";
import type { MDXComponents } from "mdx/types";
import { type ReactNode, useEffect, useMemo, useState } from "react";

import { type DefeatDoc, defeatDoc } from "src/client/ai/content/defeat";
import { type VictoryDoc, victoryDoc } from "src/client/ai/content/victory";
import { pickLocale } from "src/client/ai/content/rules";
import type { RunEnding } from "./defeat";
import { Kicker, PlateShell, Reward, SHAPES, StatRow, Stats, Token } from "./Celebration";
import { Btn, styled } from "./kit";

/**
 * A paragraph of the document, at a reading measure.
 *
 * Left-aligned inside a centred plate: this is three sentences somebody
 * actually reads, and centred prose past one line is hard work.
 */
const Copy = styled("p")(({ theme }) => ({
  maxWidth: "40ch",
  margin: 0,
  textAlign: "left",
  fontFamily: theme.kit.font.body,
  fontSize: 13.5,
  lineHeight: 1.55,
  color: theme.kit.color.inkDim
}));

const Emphasis = styled("strong")(({ theme }) => ({
  color: theme.kit.color.ink,
  fontWeight: 700,
  fontStyle: "normal"
}));

/** The document's own column, so its paragraphs stack like the plate's rows. */
const Prose = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.kit.space.sm,
  alignItems: "center"
}));

/** Two ways out, side by side. */
const Ways = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.kit.space.sm,
  marginTop: theme.kit.space.sm,
  flexWrap: "wrap",
  justifyContent: "center"
}));

/**
 * How the document's markdown lands on the plate.
 *
 * Set here rather than in the `.mdx` for the same reason `RulesPage` does it:
 * a translator gets a file of sentences, and every locale looks the same
 * without anybody remembering to make it so. The heading is the plate's big
 * line and the paragraphs are its body — so the writer's `#` and blank lines
 * are the whole of the layout language they need.
 */
function copyFor(won: boolean): MDXComponents {
  return {
    // The kicker comes from the document too, so *every* word on this plate is
    // in the file a translator is handed — including the one naming the ending.
    //
    // **Its tone is the ending's**, which is why this is a function and not a
    // constant: the brand orange is the colour this game says good news in,
    // and "Checkmate" is not good news.
    h2: (props) => (
      <Kicker {...props} role="display" tone={won ? "brand" : "rust"} size={12} weight={700} />
    ),
    h1: (props) => <Reward {...props} />,
    p: (props) => <Copy {...props} />,
    strong: (props) => <Emphasis {...props} />,
    em: (props) => <Emphasis {...props} as="em" />
  };
}

export function RunOverOverlay({
  ending,
  onRestart,
  onExit
}: {
  ending: RunEnding;
  /**
   * Start again, and back to the front door.
   *
   * Optional because the shell's own are — and a button with no handler is not
   * rendered rather than rendered dead. Both are always supplied by the only
   * thing that launches a run, so this is a guard rather than a case.
   */
  onRestart?(): void;
  onExit?(): void;
}) {
  // **A render function rather than the component.** The two documents take
  // different props — defeat keys off a `reason`, victory has nothing to
  // branch on — so a union of the two component types has no call signature
  // anything can satisfy. Binding each to its own props at load time keeps the
  // call site down to one line and loses no checking.
  const [Doc, setDoc] = useState<(() => ReactNode) | null>(null);
  const won = ending.won;
  const { reason, stage } = ending;
  const copy = useMemo(() => copyFor(won), [won]);

  useEffect(() => {
    let live = true;
    // The same preference the rules page reads, rather than a second one.
    const locale = pickLocale(
      typeof navigator === "undefined" ? [] : navigator.languages ?? []
    );
    const load = won ? victoryDoc(locale.code) : defeatDoc(locale.code);
    load()
      .then((module) => {
        if (!live) return;
        const Rendered = won
          ? () => {
              const D = module.default as VictoryDoc;
              return <D components={copy} stage={stage} />;
            }
          : () => {
              const D = module.default as DefeatDoc;
              return <D components={copy} reason={reason ?? "mated"} stage={stage} />;
            };
        setDoc(() => Rendered);
      })
      // A chunk load can fail — an offline player, or a deploy that replaced
      // the file mid-run. The plate still comes up with its buttons on it,
      // because being unable to leave a finished game is far worse than being
      // told about it in fewer words.
      .catch(() => live && setDoc(null));
    return () => {
      live = false;
    };
  }, [won, reason, stage, copy]);

  // Escape goes to the menu, and Enter starts again.
  //
  // Deliberately *not* the celebration's "any key takes it": there are two
  // outcomes here and neither is the obvious one, so a single key that picked
  // for you would throw away a run's worth of context on a stray press.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && onExit) {
        event.preventDefault();
        onExit();
      } else if (event.key === "Enter" && onRestart) {
        event.preventDefault();
        onRestart();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onExit, onRestart]);

  return (
    <PlateShell
      testid="run-over"
      plateTestid="run-over-plate"
      label={won ? "The run is won" : "The run is over"}
      // **The tone is the whole difference.** A win wants the bounce and the
      // shine that a celebration already is; a defeat wants neither, because
      // what it announces is not good news.
      tone={won ? "reward" : "grave"}
      // **No `onClose`**, so neither the backdrop nor Escape closes this from
      // MUI's side — the listener above owns Escape, and there is no dismiss.
    >
      {/* A run is won by shipping something *off* the board, so the winning
          token leans upward; a lost one is the stage plate, fallen. */}
      <Token
        shape={won ? SHAPES.rocket : SHAPES.ground}
        still
        dull={!won}
      />

      {Doc && (
        <Prose>
          <MDXProvider components={copy}>
            <Doc />
          </MDXProvider>
        </Prose>
      )}

      {/* The front it ended on. The same numbers a cleared stage reports, in
          the same places — what a run cost is the same question whether you
          won it or not. */}
      <Stats data-testid="run-over-stats">
        {ending.stats.map((stat) => (
          <StatRow key={stat.id} {...stat} />
        ))}
      </Stats>

      <Ways>
        {/* Starting again is the bright one: somebody who has just lost a run
            is far more likely to want another than to want the menu, and the
            menu is the thing you can always get to anyway. */}
        {onRestart && (
          <Btn
            variant="primary"
            data-testid="run-over-restart"
            onClick={onRestart}
            autoFocus
          >
            {won ? "Play again" : "New run"}
          </Btn>
        )}
        {onExit && (
          <Btn variant="ghost" data-testid="run-over-menu" onClick={onExit}>
            Main menu
          </Btn>
        )}
      </Ways>
    </PlateShell>
  );
}
